/**
 * 1stbuddha — header.js
 *
 * <header-component> (sections/header.liquid)
 *   Sticky behaviour when [data-sticky] is set: hides after scrolling down
 *   past 120px, reappears on scroll up (> 8px). Never hides while a dialog,
 *   mega menu or dropdown is open, or while keyboard focus is in the header.
 *   Publishes the visible header height as --header-offset (rem) on <html> so
 *   other sticky elements (PDP info column, PLP toolbar) can sit below it.
 *
 * <mega-menu> (snippets/mega-menu.liquid)
 *   Disclosure for a top-level nav link with children. Opens on hover intent
 *   (120ms) or click / Enter / Space / ↓; closes on pointer leave (150ms),
 *   Esc (focus returns to the toggle), outside click or focus leaving.
 */
(() => {
  const HIDE_AFTER = 120;
  const SCROLL_THRESHOLD = 8;
  const OPEN_DELAY = 120;
  const CLOSE_DELAY = 150;

  if (!customElements.get('header-component')) {
    class HeaderComponent extends HTMLElement {
      constructor() {
        super();
        this.onScroll = this.onScroll.bind(this);
      }

      connectedCallback() {
        this.lastY = window.scrollY;

        if (this.hasAttribute('data-sticky')) {
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.addEventListener('focusin', () => this.show());
        }

        this.resizeObserver = new ResizeObserver(() => this.publishOffset());
        this.resizeObserver.observe(this);
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        this.resizeObserver?.disconnect();
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          this.update();
        });
      }

      update() {
        const y = window.scrollY;

        if (this.isBusy()) {
          this.lastY = y;
          return;
        }
        if (Math.abs(y - this.lastY) < SCROLL_THRESHOLD) return;

        if (y > this.lastY && y > HIDE_AFTER) {
          this.hide();
        } else {
          this.show();
        }
        this.lastY = y;
      }

      isBusy() {
        return Boolean(
          document.querySelector('dialog[open]') ||
            this.querySelector('[aria-expanded="true"]') ||
            this.querySelector(':focus-visible')
        );
      }

      hide() {
        if (this.dataset.hidden === 'true') return;
        this.dataset.hidden = 'true';
        this.publishOffset();
      }

      show() {
        if (this.dataset.hidden !== 'true') return;
        this.dataset.hidden = 'false';
        this.publishOffset();
      }

      publishOffset() {
        const height = this.dataset.hidden === 'true' ? 0 : this.offsetHeight;
        const rootSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
        document.documentElement.style.setProperty('--header-offset', `${(height / rootSize).toFixed(2)}rem`);
      }
    }

    customElements.define('header-component', HeaderComponent);
  }

  if (!customElements.get('mega-menu')) {
    class MegaMenu extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('.mega-menu__toggle');
        this.panel = this.querySelector('.mega-menu__panel');
        if (!this.toggle || !this.panel) return;

        this.onDocumentClick = (event) => {
          if (!this.contains(event.target)) this.close();
        };

        this.toggle.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));

        this.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(this.closeTimer);
          this.openTimer = setTimeout(() => this.open(), OPEN_DELAY);
        });

        this.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(this.openTimer);
          this.closeTimer = setTimeout(() => this.close(), CLOSE_DELAY);
        });

        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.isOpen) {
            event.preventDefault();
            this.close();
            this.toggle.focus();
          }
          if (event.key === 'ArrowDown' && event.target === this.toggle) {
            event.preventDefault();
            this.open();
            this.panel.querySelector('a')?.focus();
          }
        });

        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.close();
        });
      }

      disconnectedCallback() {
        clearTimeout(this.openTimer);
        clearTimeout(this.closeTimer);
        document.removeEventListener('click', this.onDocumentClick);
      }

      get isOpen() {
        return this.toggle.getAttribute('aria-expanded') === 'true';
      }

      open() {
        if (this.isOpen) return;
        document.querySelectorAll('mega-menu').forEach((menu) => menu !== this && menu.close());
        this.toggle.setAttribute('aria-expanded', 'true');
        this.panel.dataset.open = 'true';
        document.addEventListener('click', this.onDocumentClick);
      }

      close() {
        clearTimeout(this.openTimer);
        if (!this.isOpen) return;
        this.toggle.setAttribute('aria-expanded', 'false');
        delete this.panel.dataset.open;
        document.removeEventListener('click', this.onDocumentClick);
      }
    }

    customElements.define('mega-menu', MegaMenu);
  }
})();
